import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, CheckCircle2, XCircle, PackageCheck } from "lucide-react";

// HQ page — list Store's Stock Requests, approve into Stock Transfers.
export default function StockRequests() {
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [status, setStatus] = useState("pending");
  const [approving, setApproving] = useState(null);   // { req, source_warehouse_id }
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const r = await api.get("/pos/stock-requests", { params });
      setRows(r.data || []);
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/production/warehouses").then((r) => setWarehouses((r.data || []).filter((w) => w.is_active !== false)));
  }, []);

  const approve = async () => {
    if (!approving.source_warehouse_id) return toast.error("Pick a source warehouse");
    try {
      const r = await api.post(`/pos/stock-requests/${approving.req.id}/approve`, null, {
        params: { source_warehouse_id: approving.source_warehouse_id },
      });
      toast.success(`Stock transfer ${r.data.transfer_no} created`);
      setApproving(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };

  const reject = async (req) => {
    const reason = window.prompt("Reject reason?");
    if (reason === null) return;
    await api.post(`/pos/stock-requests/${req.id}/reject`, null, { params: { reason } });
    toast.success("Rejected"); load();
  };

  return (
    <div className="space-y-4">
      <PageSection
        title="Stock Requests from Stores"
        description="Requests raised by Branch/Franchise POS terminals. Approve to auto-create a Stock Transfer draft from the picked HQ warehouse into the requesting store."
      >
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 h-9 rounded-md border border-gray-200 p-0.5 bg-gray-50">
              {[
                { v: "pending", label: "Pending" },
                { v: "approved", label: "Approved" },
                { v: "dispatched", label: "Dispatched" },
                { v: "received", label: "Received" },
                { v: "rejected", label: "Rejected" },
                { v: "", label: "All" },
              ].map((t) => (
                <button key={t.v} onClick={() => setStatus(t.v)} data-testid={`sr-tab-${t.v || "all"}`}
                  className={`px-3 rounded text-xs font-semibold ${status === t.v ? "bg-white text-[#9B1C1C] shadow-sm" : "text-gray-500 hover:text-gray-900"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-500 ml-auto">{rows.length} request(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2 w-32">Req #</th>
                <th className="px-3 py-2 w-40">Date</th>
                <th className="px-3 py-2">Store</th>
                <th className="px-3 py-2">Raised By</th>
                <th className="px-3 py-2 text-right w-16">Lines</th>
                <th className="px-3 py-2 w-32">Transfer</th>
                <th className="px-3 py-2 w-24">Status</th>
                <th className="px-3 py-2 w-40 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-3 py-14 text-center text-gray-400"><Truck className="h-8 w-8 mx-auto mb-2 opacity-30" />No requests.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 align-top" data-testid={`sr-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-[#9B1C1C]">{r.req_no}</td>
                  <td className="px-3 py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs">
                    <div>{r.store_type === "franchise" ? "🏪" : "🏢"} {r.store_id}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-widest">{r.store_type}</div>
                    {r.notes && <div className="text-[11px] text-gray-500 italic mt-0.5">{r.notes}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.created_by_name || "—"}</td>
                  <td className="px-3 py-2 text-right text-xs">{(r.lines || []).length}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.transfer_no ? (
                      <a href="/sales/stock-transfers" className="font-mono text-[#9B1C1C] underline">{r.transfer_no}</a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.status === "pending" && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7" onClick={() => setApproving({ req: r, source_warehouse_id: warehouses[0]?.id || "" })} data-testid={`sr-approve-${r.id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => reject(r)} data-testid={`sr-reject-${r.id}`}>
                          <XCircle className="h-3 w-3 mr-1" />Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </PageSection>

      {/* Approve dialog */}
      <Dialog open={!!approving} onOpenChange={(v) => !v && setApproving(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Approve Stock Request</DialogTitle></DialogHeader>
          {approving && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                Approving <b className="font-mono">{approving.req.req_no}</b> — creates a draft Stock Transfer with the requested lines.
              </div>
              <label className="text-xs">
                <span className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Source Warehouse *</span>
                <select value={approving.source_warehouse_id} onChange={(e) => setApproving({ ...approving, source_warehouse_id: e.target.value })}
                  className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="sr-source-wh">
                  <option value="">— Pick HQ warehouse —</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>🏭 {w.name}</option>)}
                </select>
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-100 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50"><tr className="text-left text-[10px] uppercase tracking-widest text-gray-500"><th className="px-2 py-1">Product</th><th className="px-2 py-1 text-right w-16">Qty</th></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {(approving.req.lines || []).map((l, i) => (
                      <tr key={i}><td className="px-2 py-1"><b>{l.product_name}</b><div className="font-mono text-[10px] text-gray-500">{l.product_sku}</div></td><td className="px-2 py-1 text-right tabular-nums">{l.qty}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproving(null)}>Cancel</Button>
            <Button onClick={approve} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sr-approve-confirm">
              <PackageCheck className="h-4 w-4 mr-1" />Create Transfer Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:    { tone: "bg-amber-50 text-amber-700 border-amber-200",       label: "Pending" },
    approved:   { tone: "bg-blue-50 text-blue-700 border-blue-200",           label: "Approved" },
    dispatched: { tone: "bg-indigo-50 text-indigo-700 border-indigo-200",     label: "Dispatched" },
    received:   { tone: "bg-emerald-50 text-emerald-700 border-emerald-200",  label: "Received" },
    rejected:   { tone: "bg-red-50 text-red-700 border-red-200",              label: "Rejected" },
  };
  const s = map[status] || { tone: "bg-gray-100 text-gray-700 border-gray-200", label: status };
  return <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-1.5 py-0.5 ${s.tone}`}>{s.label}</span>;
}
